

import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { format, isValid, parseISO, parse as dateFnsParse, startOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { AppMenuItem } from "./menu-config";


export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formats a date string or Date object to 'yyyy-MM-dd' for storing in react-hook-form state.
 * Ensures the date is treated as local.
 * @param date The date to format (string or Date object or null/undefined).
 * @returns The formatted date string "yyyy-MM-dd" or null if the date is invalid/empty.
 */
export function formatDateToInput(date: string | Date | null | undefined): string | null {
  if (!date) return null;
  
  let dateObj: Date;

  if (typeof date === 'string') {
    if (date.match(/^\d{4}-\d{2}-\d{2}$/)) { // Input is "yyyy-MM-dd"
      const [year, monthStr, dayStr] = date.split('-').map(Number);
      // Create date in local timezone. JS month is 0-indexed.
      dateObj = new Date(year, monthStr - 1, dayStr);
      // Verify if the created date is valid and matches the input to avoid invalid dates like 2023-02-30
      if (dateObj.getFullYear() !== year || dateObj.getMonth() !== monthStr - 1 || dateObj.getDate() !== dayStr) {
        console.warn(`[formatDateToInput] Invalid date components from "yyyy-MM-dd" string: ${date}`);
        return null;
      }
    } else if (date.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) { // Input is "dd/MM/yyyy" or "d/M/yyyy"
      dateObj = dateFnsParse(date, "dd/MM/yyyy", new Date()); // Prefer dd/MM/yyyy
       if (!isValid(dateObj)) { 
            dateObj = dateFnsParse(date, "d/M/yyyy", new Date()); // Fallback for d/M/yyyy
       }
    } else {
      // Attempt to parse other ISO-like formats, but be wary of UTC interpretation
      dateObj = parseISO(date);
      // If parseISO interpreted a date-only string (no 'T' or 'Z') as UTC midnight,
      // it might be off by a day depending on local timezone. Re-construct as local.
      if (date.match(/^\d{4}-\d{2}-\d{2}(?!T)/)) {
          const [year, monthStr, dayStr] = date.split('-').map(Number);
          dateObj = new Date(year, monthStr - 1, dayStr);
          if (dateObj.getFullYear() !== year || dateObj.getMonth() !== monthStr - 1 || dateObj.getDate() !== dayStr) {
            console.warn(`[formatDateToInput] Invalid date components after re-parsing ISO-like date-only string: ${date}`);
            return null;
          }
      }
    }
  } else { // It's already a Date object
    dateObj = date;
  }

  if (isValid(dateObj)) {
    // Construct the "yyyy-MM-dd" string manually from the Date object's local components
    const year = dateObj.getFullYear();
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0'); // getMonth is 0-indexed
    const day = dateObj.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  
  console.warn(`[formatDateToInput] Could not parse or invalid date received:`, date);
  return null;
}


/**
 * Generates initials from a name or email.
 * @param name Optional name string.
 * @param email Optional email string (used if name is not provided).
 * @returns Initials string.
 */
export function getInitials(name?: string | null, email?: string | null): string {
  if (name) {
    const names = name.trim().split(' ');
    if (names.length === 1 && names[0].length > 0) return names[0].charAt(0).toUpperCase();
    if (names.length > 1) return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
    if (names[0]?.length > 0) return names[0].charAt(0).toUpperCase(); // case of single name after trim like "  John  "
  }
  if (email) {
    return email.charAt(0).toUpperCase();
  }
  return 'P'; // Default
}

/**
 * Masks a CPF string.
 * @param cpf The CPF string (can contain non-digits).
 * @returns The masked CPF string (e.g., 000.000.000-00).
 */
export function maskCPF(cpf: string): string {
  const cleaned = unmask(cpf);
  if (cleaned.length > 11) return cpf.substring(0, 14); // Prevent overly long inputs
  return cleaned
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

/**
 * Masks a CNPJ string.
 * @param cnpj The CNPJ string (can contain non-digits).
 * @returns The masked CNPJ string (e.g., 00.000.000/0000-00).
 */
export function maskCNPJ(cnpj: string): string {
  const cleaned = unmask(cnpj);
   if (cleaned.length > 14) return cnpj.substring(0, 18); // Prevent overly long inputs
  return cleaned
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

/**
 * Masks a CEP string.
 * @param cep The CEP string (can contain non-digits).
 * @returns The masked CEP string (e.g., 00000-000).
 */
export function maskCEP(cep: string): string {
  const cleaned = unmask(cep);
  if (cleaned.length > 8) return cep.substring(0, 9);
  return cleaned.replace(/(\d{5})(\d{1,3})$/, '$1-$2');
}

/**
 * Masks a phone number string (supports 10 or 11 digits).
 * @param phone The phone number string (can contain non-digits).
 * @returns The masked phone number string (e.g., (00) 0000-0000 or (00) 00000-0000).
 */
export function maskPhone(phone: string): string {
  const cleaned = unmask(phone);
  if (cleaned.length > 11) return phone.substring(0,15); // Limit length of masked output

  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  } else if (cleaned.length === 10) {
    return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  // For partial input, format as much as possible
  if (cleaned.length > 6) { // e.g. (XX) XXXXX- or (XX) XXXX-
    return `(${cleaned.slice(0,2)}) ${cleaned.slice(2,cleaned.length <= 10 ? 6 : 7)}${cleaned.length > (cleaned.length <= 10 ? 6 : 7) ? '-' : ''}${cleaned.slice(cleaned.length <= 10 ? 6 : 7)}`;
  } else if (cleaned.length > 2) { // e.g. (XX) X
    return `(${cleaned.slice(0,2)}) ${cleaned.slice(2)}`;
  } else if (cleaned.length > 0) { // e.g. (X
    return `(${cleaned}`;
  }
  return cleaned;
}

/**
 * Removes all non-digit characters from a string.
 * @param value The string to unmask.
 * @returns A string containing only digits.
 */
export function unmask(value: string | null | undefined): string {
  if (!value) return '';
  return String(value).replace(/\D/g, '');
}

// Helper function to parse HSL string like "207 82% 70%" or "207, 82%, 70%"
function parseHslString(hslString: string): [number, number, number] | null {
  const cleanedString = hslString.trim();
  const match = cleanedString.match(/^(\d{1,3})\s*,?\s*(\d{1,3})%?\s*,?\s*(\d{1,3})%?$/);
  if (match) {
    return [parseInt(match[1], 10), parseInt(match[2], 10), parseInt(match[3], 10)];
  }
  return null;
}

// Helper function to convert HSL to RGB
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  s /= 100;
  l /= 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) =>
    l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [
    Math.round(255 * f(0)),
    Math.round(255 * f(8)),
    Math.round(255 * f(4)),
  ];
}

export function getPrimaryColorRGBFromCSS(): [number, number, number] {
  const defaultPrimaryRGB: [number, number, number] = [128, 128, 128]; // Cinza #808080
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return defaultPrimaryRGB;
  }
  try {
    const primaryColorHSLString = getComputedStyle(document.documentElement).getPropertyValue('--primary').trim();
    if (primaryColorHSLString) {
      const hslValues = parseHslString(primaryColorHSLString);
      if (hslValues) {
        return hslToRgb(hslValues[0], hslValues[1], hslValues[2]);
      } else {
        console.warn(`[getPrimaryColorRGBFromCSS] Não foi possível parsear a string HSL da variável --primary: "${primaryColorHSLString}". Usando cor padrão.`);
      }
    } else {
      console.warn("[getPrimaryColorRGBFromCSS] Variável CSS --primary não encontrada ou vazia. Usando cor padrão.");
    }
  } catch (error) {
    console.error("[getPrimaryColorRGBFromCSS] Erro ao tentar obter a cor primária do CSS:", error);
  }
  return defaultPrimaryRGB;
}

export function flattenMenuItems(
  items: AppMenuItem[],
  isAdmin: boolean,
  parentPath: string[] = []
): (AppMenuItem & { breadcrumb: string[] })[] {
  let flattened: (AppMenuItem & { breadcrumb: string[] })[] = [];
  
  const visibleItems = items.filter(item => !(item.adminOnly && !isAdmin));

  visibleItems.forEach(item => {
    const newPath = [...parentPath, item.label];
    
    flattened.push({ ...item, breadcrumb: parentPath });

    if (item.subItems && item.subItems.length > 0) {
      flattened = flattened.concat(flattenMenuItems(item.subItems, isAdmin, newPath));
    }
  });
  return flattened;
}
